import { ActionTree } from 'vuex'

import api from '@/apiClient'

import {
  DeliverySettingsResourcePayload,
  Credentials,
  BusinessUnit,
  ShopSettingsResourcePayload
} from '@/models'

import { assertToken } from '@/utils'

import {
  DECREMENT_MODAL_COUNTER,
  INCREMENT_MODAL_COUNTER,
  UPDATE_BUSINESS_UNIT,
  UPDATE_DELIVERY_SETTINGS,
  UPDATE_PROFILE,
  UPDATE_SHOP_SETTINGS
} from './mutations'

import { RootState } from './state'

const actions: ActionTree<RootState, RootState> = {
  /**
   * Creates a DeliverySettings.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the DeliverySettings.
   */
  async createDeliverySettings ({ commit, rootState, rootGetters, state }, payload: DeliverySettingsResourcePayload) {
    const providerId: number | null = rootGetters['auth/currentProvider']
    const businessUnit = rootState.businessUnit

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const deliverySettings = await api.menu.deliverySettings.createDeliverySettings(tokenV2, providerId, businessUnit.id, payload)

    commit(UPDATE_DELIVERY_SETTINGS, deliverySettings)

    return deliverySettings
  },

  /**
   * Creates a DeliverySettings if it does not exist, or updates it.
   */
  async createOrUpdateDeliverySettings ({ dispatch, state }, payload: DeliverySettingsResourcePayload) {
    if (state.deliverySettings !== null) {
      await dispatch('updateDeliverySettings', {
        id: state.deliverySettings.id,
        payload
      })
    } else {
      await dispatch('createDeliverySettings', payload)
    }
  },

  /**
   * Fetches the DeliverySettings.
   *
   * It rejects if the user is not logged in.
   */
  async loadDeliverySettings ({ commit, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const deliverySettings = await api.menu.deliverySettings.fetchDeliverySettings(tokenV2, providerId, businessUnit.id)

    commit(UPDATE_DELIVERY_SETTINGS, deliverySettings)
  },

  /**
   * Creates a ShopSettings.
   *
   * It rejects if the user is not logged in.
   *
   * @param payload Payload to create the ShopSettings.
   */
  async createShopSettings ({ commit, rootState, rootGetters, state }, payload: ShopSettingsResourcePayload) {
    const providerId: number | null = rootGetters['auth/currentProvider']
    const businessUnit = rootState.businessUnit

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const shopSettings = await api.menu.shopSettings.createShopSettings(tokenV2, providerId, businessUnit.id, payload)

    commit(UPDATE_SHOP_SETTINGS, shopSettings)

    return shopSettings
  },

  /**
   * Creates a ShopSettings if it does not exist, or updates it.
   */
  async createOrUpdateShopSettings ({ dispatch, state }, payload: ShopSettingsResourcePayload) {
    if (state.shopSettings !== null) {
      await dispatch('updateShopSettings', {
        id: state.shopSettings.id,
        payload
      })
    } else {
      await dispatch('createShopSettings', payload)
    }
  },

  /**
   * Fetches the ShopSettings.
   *
   * It rejects if the user is not logged in.
   */
  async loadShopSettings ({ commit, rootState, rootGetters, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const shopSettings = await api.menu.shopSettings.fetchShopSettings(tokenV2, providerId, businessUnit.id)

    commit(UPDATE_SHOP_SETTINGS, shopSettings)
  },

  /**
   * Loads the businessUnit.
   *
   * It should be dispached only if the user is logged in.
   */
  async loadBusinessUnit ({ commit, dispatch, rootGetters, state }) {
    const token = state.auth.token
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (token === null || providerId === null) {
      throw new Error('User is not authenticated')
    }

    try {
      const businessUnitString = localStorage.getItem('businessUnit')
      let businessUnit: BusinessUnit

      if (businessUnitString) {
        businessUnit = JSON.parse(businessUnitString)
      } else {
        const businessUnits = await api.businessUnit.fetchBusinessUnitList(token, providerId)
        businessUnit = businessUnits[0]
        localStorage.setItem('businessUnit', JSON.stringify(businessUnit))
      }

      commit(UPDATE_BUSINESS_UNIT, businessUnit)
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  /**
   * Fetches the user Profile.
   *
   * It rejects if the user is not logged in.
   */
  async loadProfile ({ commit, dispatch, state }) {
    try {
      const token = assertToken(state.auth.token)
      const profile = await api.profile.fetchProfile(token)

      commit(UPDATE_PROFILE, profile)
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  /**
   * Authenticates with the API, fetches all Tokens and Profile.
   *
   * @param credentials User email and password.
   */
  async login ({ dispatch }, credentials: Credentials) {
    await dispatch('auth/login', credentials)
    await dispatch('loadProfile')
    await dispatch('loadBusinessUnit')
  },

  /**
   * Clears the Store.
   */
  async logout ({ commit, dispatch }) {
    await dispatch('auth/logout')
    await dispatch('menu/logout')
    await dispatch('ordering/logout')
    await dispatch('qrCodes/logout')
    await dispatch('unloadBusinessUnit')

    commit(UPDATE_DELIVERY_SETTINGS, null)
    commit(UPDATE_BUSINESS_UNIT, null)
    commit(UPDATE_PROFILE, null)
    commit(UPDATE_SHOP_SETTINGS, null)

    window.location.replace(`${window.location.origin}/login`)
  },

  /**
   * Updates the DeliverySettings.
   *
   * It rejects if the user is not logged in.
   *
   * @param id DeliverySettings id to be updated.
   * @param payload Payload to update it.
   */
  async updateDeliverySettings (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: DeliverySettingsResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const deliverySettings = await api.menu.deliverySettings.updateDeliverySettings(tokenV2, providerId, businessUnit.id, id, payload)

    commit(UPDATE_DELIVERY_SETTINGS, deliverySettings)
  },

  /**
   * Updates the ShopSettings.
   *
   * It rejects if the user is not logged in.
   *
   * @param id ShopSettings id to be updated.
   * @param payload Payload to update it.
   */
  async updateShopSettings (
    { commit, rootState, rootGetters, state },
    { id, payload }: { id: number; payload: ShopSettingsResourcePayload }
  ) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const tokenV2 = assertToken(state.auth.tokenV2)

    const shopSettings = await api.menu.shopSettings.updateShopSettings(tokenV2, providerId, businessUnit.id, id, payload)

    commit(UPDATE_SHOP_SETTINGS, shopSettings)
  },

  /**
   * Updates the modalCount.
   *
   * @param isActive State of last updated modal.
   */
  async updateModalCount ({ commit }, isActive: boolean) {
    if (isActive) {
      commit(INCREMENT_MODAL_COUNTER)
    } else {
      commit(DECREMENT_MODAL_COUNTER)
    }
  },

  unloadBusinessUnit () {
    localStorage.removeItem('businessUnit')
  }
}

export default actions
